/**
 * Helper functions for voice connection operations
 */

export interface ConnectionValidationResult {
  isValid: boolean;
  reason: string;
}

/**
 * Validates connection preconditions before attempting to connect
 * @param sessionCallId - The call ID from the session state
 * @param voiceMeetingService - The voice meeting service instance
 * @returns Validation result with isValid flag and reason
 */
export const validateConnectionPreconditions = (
  sessionCallId: number | null
): ConnectionValidationResult => {
  if (!sessionCallId || sessionCallId === 0) {
    return {
      isValid: false,
      reason: 'Call ID is zero or invalid',
    };
  }

  return { isValid: true, reason: '' };
};

/**
 * Determines if an error message indicates an expected abort condition vs a real error
 * @param errorMessage - The error message to check
 * @returns True if this is an expected abort error, false if it's a real error
 */
export const isAbortError = (errorMessage: string): boolean => {
  const abortConditions = [
    'Call ID is zero or invalid',
    'Connection was aborted due to call disconnect',
    'aborted',
  ];
  return abortConditions.some((condition) => errorMessage.includes(condition));
};

/**
 * Determines if an error message indicates a warning condition (completed or not found)
 * @param errorMessage - The error message to check
 * @returns True if this is a warning error (completed/not found), false otherwise
 */
export const isWarningError = (errorMessage: string): boolean => {
  const warningConditions = ['completed', 'not found'];
  return warningConditions.some((condition) =>
    errorMessage.toLowerCase().includes(condition)
  );
};

/**
 * Creates a delay promise for backend processing
 * Do not use this for general delay use, this is to allow Switchboard to process disconnects once the call is answered.
 * @param delayMs - Delay in milliseconds
 * @returns Promise that resolves after the delay
 */
export const createBackendProcessingDelay = (
  delayMs: number
): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(resolve, delayMs);
  });
};
